import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import {
  ToAnyReachableSetsCache,
  isAttributeAcceptedForQlCondition,
} from 'cadenza/workbook/state/workbook-reachability.state';
import {
  getAttributes,
  getAttributesById,
  getNotVisibilityRestrictedObjecttypes,
  hasObjecttypeDatasourceCapabilities,
  resolveObjecttypeAttributes,
} from 'cadenza/workbook/workbook-core-selectors.state';

/**
 * A controller for conditions.
 *
 * @typedef QlConditionsController
 * @extends ConditionsController
 * @property {boolean} hasObjectTypeContextSet - information whether the controller is already used in an object type context (attributes are filtered to the specified reachable object types network)
 */
export class QlConditionController {
  readonly #condition;
  #acceptedAttributeIds;
  #contextAttributeIds: (AttributeId | ActiveAttributeId)[] = []; // attribute ids set when ot context is build by constructing the expression (first attribute is assigned, next is going to be added)
  readonly #indexProviderFn; // used for global condition reset action

  constructor(
    condition: ConditionDto,
    acceptedAttributeIds: (AttributeId | ActiveAttributeId)[],
    indexProviderFn = () => 0,
  ) {
    this.#condition = condition;
    this.#acceptedAttributeIds = acceptedAttributeIds;
    this.#indexProviderFn = indexProviderFn;
  }

  get qlCondition() {
    return this.#condition;
  }

  get qlConditionIndex() {
    return this.#indexProviderFn();
  }

  accepts(attribute: WorkbookAttributeDto) {
    // We do not filter out not usableInCondition here. The flag is handled later in data browser flyout setup for ql and the parser context.
    if (!(this.#acceptedAttributeIds.length === 0) && !this.#acceptedAttributeIds.includes(attribute.id)) {
      return { accepts: false };
    }
    if (this.#contextAttributeIds?.length > 0 && !this.#contextAttributeIds.includes(attribute.id)) {
      return { accepts: false };
    }

    // Do not include attributes from objecttypes from non SQL datasources such as Elasticsearch
    if (!hasObjecttypeDatasourceCapabilities(getStore().getState(), attribute.objecttypeId, ['QL_CONDITIONS'])) {
      return { accepts: false };
    }

    {
      return { accepts: true };
    }
  }

  getAcceptedAttributes() {
    return this.#getAcceptedAttributes(getAttributes(getStore().getState()));
  }

  getAcceptedUnrestrictedAttributes() {
    return this.#getAcceptedAttributes(this.#getAllUnrestrictedAttributes());
  }

  #getAcceptedAttributes(allAttributes: WorkbookAttributeDto[]) {
    return allAttributes.filter((attribute) => this.accepts(attribute).accepts);
  }

  #getAllUnrestrictedAttributes() {
    const visibleObjecttypes = resolveObjecttypeAttributes(
      getNotVisibilityRestrictedObjecttypes(getStore().getState()),
    );
    return visibleObjecttypes.flatMap((ot) => ot.attributes);
  }

  /**
   * Set object type context according to already used attributes.
   * For example in ql condition context you may start with no context (no MOT set),
   * but after first attribute is used in the expression, the context is set for next possible attributes.
   *
   * @param currentAttributeIds - the attribute ids to set context to
   */
  setAttributeContext(currentAttributeIds: (AttributeId | ActiveAttributeId)[]) {
    if (currentAttributeIds && currentAttributeIds.length > 0) {
      const reachableSetsCache = new ToAnyReachableSetsCache(getStore().getState());
      const currentAttributes = getAttributesById(getStore().getState(), currentAttributeIds);
      this.#contextAttributeIds = getAttributes(getStore().getState())
        .filter((candidate) => isAttributeAcceptedForQlCondition(currentAttributes, candidate, reachableSetsCache))
        .map((attribute) => attribute.id);
    } else {
      this.#contextAttributeIds = [];
    }
  }
}
